import {
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { defaults as defaultControls, ScaleLine } from "ol/control";
import { Map, MapBrowserEvent, View } from "ol";
import { fromLonLat, toLonLat } from "ol/proj";
import TileLayer from "ol/layer/Tile";
import { OSM } from "ol/source";
import { Coordinate } from "ol/coordinate";

import { useSyncMapContext } from "../hooks/incontext/useSyncContext";
import { MapContext } from "../MapContext";
import { DragRotateAndZoom } from "ol/interaction";
import { defaults as defaultInteractions } from "ol/interaction/defaults.js";
import { VectorSourceProvider } from "../hooks/incontext/useVectorSourceContext";
import { CompassWheel } from "../control";
import { useHoverCursor } from "../hooks/incontext/useHoverCursor";
import { useLazyRef } from "../hooks/useLazyRef";

/**
 * 동기화 그룹 내 개별 지도 props — 반드시 `SyncMapGroup` 자식으로 사용.
 *
 * `isDecoupled=true` 면 그룹의 center/zoom 동기화에서 분리되어 독립 제어.
 * `useMap()` 으로 OL 인스턴스 접근 가능.
 */
export interface SyncMapProps {
  /**
   * @default false
   * @description When you want to control map out
   */
  isDecoupled?: boolean;

  /**
   * @default "1000px"
   * @description This value set height css value of style. (ex. 100%, 100px, 100vh)
   */
  height?: string;

  /**
   * @default "1000px"
   * @description This value set width css value of style. (ex. 100%, 100px, 100vw)
   */
  width?: string;

  children?: ReactNode;

  onClick?: ({
    event,
    lonlat,
  }: {
    event: MapBrowserEvent<any>;
    lonlat: Coordinate;
  }) => void;

  /**
   * @default true
   * @description Show metric scale line control on the map
   */
  hasScaleLine?: boolean;
  hasCompassWheel?: boolean;
}

export const SyncMap = ({
  isDecoupled = false,
  children,
  height = "500px",
  width = "500px",
  onClick,
  hasScaleLine = true,
  hasCompassWheel = false,
}: SyncMapProps) => {
  const {
    sharedView,
    onWheelHandler,
    onZoomHandler,
    controlledCenter,
    controlledZoomLevel,
    controlledRotation,
    registerMap,
    unregisterMap,
  } = useSyncMapContext();

  const id = useId();
  const mapId = `react-openlayers-map-${id}`;
  const [isReady, setIsReady] = useState(false);
  // useRef(new Heavy()) 는 매 렌더마다 throwaway 인스턴스를 생성한다 — lazy-init 으로 최초 1회만 생성(D7).
  const osmRef = useLazyRef<TileLayer<OSM>>(
    () =>
      new TileLayer({
        source: new OSM({
          crossOrigin: "anonymous",
          attributions: [],
        }),
      })
  );

  const mapObj = useLazyRef<Map>(
    () =>
      new Map({
        controls: defaultControls({
          zoom: false,
          rotate: true,
        }).extend(
          hasScaleLine
            ? [
                new ScaleLine({
                  units: "metric", // 미터 단위 사용
                  steps: 4, // 스케일 단계 수
                  text: true, // 텍스트 표시
                  minWidth: 64, // 최소 너비
                }),
              ]
            : []
        ),
        interactions: defaultInteractions().extend([new DragRotateAndZoom()]),
        view: isDecoupled
          ? new View({
              center: fromLonLat(controlledCenter),
              zoom: controlledZoomLevel,
              rotation: (controlledRotation * Math.PI) / 180,
            })
          : sharedView,
      })
  );

  const clickEventHandler = useCallback(
    (event: MapBrowserEvent<any>) => {
      if (onClick) {
        onClick({ event, lonlat: toLonLat(event.coordinate) });
      }
    },
    [onClick]
  );

  // 맵 등록/해제
  useEffect(() => {
    registerMap(mapObj.current, isDecoupled);

    return () => {
      unregisterMap(mapObj.current);
    };
  }, [registerMap, unregisterMap, isDecoupled]);

  // isDecoupled가 true인 경우에만 개별 상태 업데이트
  useEffect(() => {
    if (isDecoupled) {
      mapObj.current.getView().setCenter(fromLonLat(controlledCenter));
    }
  }, [controlledCenter, isDecoupled]);

  useEffect(() => {
    if (isDecoupled) {
      mapObj.current?.getView().setZoom(controlledZoomLevel);
    }
  }, [controlledZoomLevel, isDecoupled]);

  useEffect(() => {
    if (isDecoupled) {
      mapObj.current
        ?.getView()
        .setRotation((controlledRotation * Math.PI) / 180);
    }
  }, [controlledRotation, isDecoupled]);

  useEffect(() => {
    const map = mapObj.current;

    function zoomHandler(e: MapBrowserEvent<any>) {
      onZoomHandler(e, map);
    }

    map.on("pointerdrag", zoomHandler);

    return () => {
      map.un("pointerdrag", zoomHandler);
    };
  }, [onZoomHandler]);

  useEffect(() => {
    const map = mapObj.current;
    map.on("singleclick", clickEventHandler);
    return () => {
      map.un("singleclick", clickEventHandler);
    };
  }, [clickEventHandler]);

  useHoverCursor(mapObj.current);

  // target 컨테이너 크기 변화 시 맵이 새 크기를 반영해 타일을 다시 표시하도록 보장하는 안전망(D1).
  useEffect(() => {
    if (!isReady) return;
    const map = mapObj.current;
    const target = map.getTargetElement();
    if (!target) return;
    const resizeObserver = new ResizeObserver(() => {
      map.updateSize();
    });
    resizeObserver.observe(target);
    return () => resizeObserver.disconnect();
  }, [isReady]);

  useLayoutEffect(() => {
    mapObj.current.addLayer(osmRef.current);
    const mapRef = mapObj.current;

    mapRef.setTarget(mapId);
    setIsReady(true);

    return () => {
      mapRef.setTarget(undefined);
      mapRef.setLayers([]);
      mapRef.dispose();
    };
  }, [mapId]);

  return (
    <VectorSourceProvider>
      <MapContext.Provider value={mapObj.current}>
        <div
          id={mapId}
          onWheel={(e) => onWheelHandler(e, mapObj.current)}
          className="react-openlayers-map-container"
          style={{ width, height }}
        >
          {isReady && hasCompassWheel && <CompassWheel />}
          {isReady && children}
        </div>
      </MapContext.Provider>
    </VectorSourceProvider>
  );
};
